<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <router-link :to="{ name: 'ClientWixtarTicketList' }" class="btn btn-secondary">
          <i class="ri-ticket-2-line"></i>
          票券管理
        </router-link>
        <button class="btn btn-secondary" :disabled="loading" @click="fetchRecords">
          <i class="ri-refresh-line"></i>
          重新整理
        </button>
      </div>
    </div>

    <div class="page-container">
      <div class="page-toolbar">
        <div class="filter-group search-box">
          <input
            v-model="searchQuery"
            type="text"
            class="form-control"
            placeholder="搜尋批次編號、票券名稱、活動名稱、派券原因..."
            @input="handleSearch"
          />
          <i class="ri-search-line"></i>
        </div>
        <div class="filter-group">
          <label class="filter-label">狀態</label>
          <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
            <option value="">全部狀態</option>
            <option value="completed">已完成</option>
            <option value="failed">失敗</option>
            <option value="processing">執行中</option>
          </select>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <p class="error-message">{{ error }}</p>
        <button class="btn btn-secondary" @click="fetchRecords">重試</button>
      </div>

      <div v-else class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>批次編號</th>
              <th>票券</th>
              <th>派券原因</th>
              <th>對象</th>
              <th>結果</th>
              <th>LINE 推播</th>
              <th>操作者</th>
              <th>派券時間</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="records.length === 0">
              <td colspan="9" class="empty-state">尚無派券紀錄</td>
            </tr>
            <tr v-for="record in records" :key="record.id">
              <td class="cell-code">{{ record.batchId }}</td>
              <td class="cell-ticket-name">
                <strong>{{ record.ticketName || `票券 ${record.ticketId}` }}</strong>
                <div v-if="record.activitySubject" class="cell-sub">{{ record.activitySubject }}</div>
              </td>
              <td>{{ record.reason || '-' }}</td>
              <td>{{ record.totalTargets }}</td>
              <td>
                <span class="text-success">成功 {{ record.successCount }}</span>
                <span v-if="record.failedCount > 0" class="text-danger">／失敗 {{ record.failedCount }}</span>
              </td>
              <td>
                <template v-if="record.sendLineNotification">
                  已推播 {{ record.notificationSentCount }}
                  <div v-if="record.lineMsgTemplateName" class="cell-sub">{{ record.lineMsgTemplateName }}</div>
                </template>
                <span v-else>未推播</span>
              </td>
              <td>{{ record.createdByName || record.createdByUsername || '-' }}</td>
              <td>{{ formatDateTime(record.createdAt) }}</td>
              <td>
                <router-link
                  :to="{ name: 'ClientWixtarDispatchDetail', params: { batchId: record.batchId } }"
                  class="btn btn-sm btn-secondary"
                >
                  <i class="ri-file-list-line"></i>
                  詳情
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>

        <ListPagination
          v-if="pagination.totalPages > 1"
          :page="currentPage"
          :total-pages="pagination.totalPages"
          :total="pagination.totalItems"
          @change="goToPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { wixtarService } from '@/api/modules/wixtar.js';

/** 搜尋輸入的節流時間，避免每個字元都打一次 API */
const SEARCH_DEBOUNCE_MS = 400;

export default {
  name: 'WixtarDispatchList',
  setup() {
    const { pageTitle } = usePageTitle();

    const records = ref([]);
    const loading = ref(false);
    const error = ref('');
    const searchQuery = ref('');
    const filterStatus = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const pagination = reactive({ totalPages: 0, totalItems: 0 });

    let searchTimer = null;

    const fetchRecords = async () => {
      loading.value = true;
      error.value = '';

      try {
        const response = await wixtarService.getDispatchRecords({
          page: currentPage.value,
          pageSize: pageSize.value,
          search: searchQuery.value.trim() || undefined,
          status: filterStatus.value || undefined
        });

        if (response?.success) {
          records.value = response.data.items || [];
          pagination.totalPages = response.data.pagination?.totalPages || 0;
          pagination.totalItems = response.data.pagination?.totalItems || 0;
        } else {
          error.value = response?.message || '查詢派券紀錄失敗';
        }
      } catch (err) {
        console.error('查詢派券紀錄失敗:', err);
        error.value = err.message || '查詢派券紀錄失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimer) clearTimeout(searchTimer);
      searchTimer = setTimeout(() => {
        currentPage.value = 1;
        fetchRecords();
      }, SEARCH_DEBOUNCE_MS);
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchRecords();
    };

    const goToPage = (page) => {
      currentPage.value = page;
      fetchRecords();
    };

    const formatDateTime = (value) => {
      if (!value) return '-';
      return new Date(value).toLocaleString('zh-TW');
    };

    onMounted(fetchRecords);

    return {
      pageTitle,
      records,
      loading,
      error,
      searchQuery,
      filterStatus,
      currentPage,
      pagination,
      fetchRecords,
      handleSearch,
      handleFilterChange,
      goToPage,
      formatDateTime
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

/* 9 個欄位，手機版需可橫向捲動 */
.table-wrapper table {
  min-width: 1200px;
}

.cell-code {
  font-family: 'SFMono-Regular', Consolas, monospace;
  font-size: 12.5px;
  color: #495057;
}

.cell-ticket-name strong {
  display: block;
  font-weight: 600;
  color: #333;
}

.cell-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #6c757d;
}

.text-success {
  color: #15803d;
  font-weight: 600;
}

.text-danger {
  color: #b91c1c;
  font-weight: 600;
}

.loading-state,
.error-state {
  padding: 48px 20px;
  text-align: center;
  color: #6c757d;
}

.error-message {
  color: #b91c1c;
  margin-bottom: 12px;
}

.empty-state {
  padding: 32px 20px;
  text-align: center;
  color: #6c757d;
}

.spinner {
  width: 32px;
  height: 32px;
  margin: 0 auto 12px;
  border: 3px solid #e9ecef;
  border-top-color: #1890ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
